import { computed, type Ref } from 'vue';
import { RelationshipStatus } from '@/helpers/const';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import { defaultApplicant, createFinancials } from '@/helpers/builder';
import type { Applicant, ApplicantEntry, ApplicantFinancials, UIRelationshipStatus } from '@/types';

// Shape that the relationship manager requires
type RelationshipManagerApplicant = {
  id: string;
  type?: string;
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  relationshipStatus?: string;
};

/**
 * Prepare financials object for all applicants
 */
export function prepareFinancials(
  allApplicants: ApplicantEntry[],
  originalFinancials: ApplicantFinancials,
): ApplicantFinancials {
  return allApplicants.reduce((fin, app) => {
    fin[app.id] = originalFinancials[app.id] ?? createFinancials();
    return fin;
  }, {} as ApplicantFinancials);
}

/**
 * Transform API relationship format to UI format
 */
export function transformApiToUiFormat(applicants: Applicant[]): ApplicantEntry[] {
  return applicants.map((applicant) => {
    const transformed = { ...applicant } as ApplicantEntry;

    // Transform API relationship format back to UI format
    if (applicant.relationshipStatus === RelationshipStatus.MARRIED_OR_DEFACTO) {
      if (applicant.relatedApplicantIndex !== undefined && applicant.relatedApplicantIndex >= 0) {
        // Married to specific applicant
        const partnerId = applicants[applicant.relatedApplicantIndex]?.id;
        if (partnerId) {
          transformed.relationshipStatus = `married_to_${partnerId}` as UIRelationshipStatus;
        } else {
          transformed.relationshipStatus = 'married_to_applicant';
        }
      } else {
        // Married to non-applicant
        transformed.relationshipStatus = 'married_to_non_applicant';
      }
    } else {
      // Single
      transformed.relationshipStatus = 'single';
    }

    return transformed;
  });
}

/**
 * Transform UI relationship format back to API format
 */
export function transformRelationshipStatuses(applicants: ApplicantEntry[]): Applicant[] {
  return applicants.map((applicant) => {
    const transformed = Object.assign(defaultApplicant(), applicant);
    const uiRelationshipStatus = applicant.relationshipStatus as string | undefined;

    // Hard stop: UI placeholder must not be persisted
    if (uiRelationshipStatus === 'married_to_applicant') {
      throw new Error(`[${applicant.id}] Relationship placeholder must be resolved before saving.`);
    }

    if (uiRelationshipStatus === 'married_to_non_applicant') {
      transformed.relationshipStatus = RelationshipStatus.MARRIED_OR_DEFACTO;
      transformed.relatedApplicantIndex = undefined;
      return transformed;
    }

    if (uiRelationshipStatus?.startsWith('married_to_')) {
      const partnerId = uiRelationshipStatus.replace('married_to_', '');

      // disallow self-marriage
      if (partnerId === applicant.id) {
        throw new Error(`[${applicant.id}] Cannot be married to oneself.`);
      }

      // must be an actual applicant id if it reached here
      const partnerIndex = applicants.findIndex((a) => a.id === partnerId);
      if (partnerIndex < 0) {
        throw new Error(`[${applicant.id}] Partner ${partnerId} not found.`);
      }

      transformed.relationshipStatus = RelationshipStatus.MARRIED_OR_DEFACTO;
      transformed.relatedApplicantIndex = partnerIndex;
      return transformed;
    }

    // Default single
    transformed.relationshipStatus = RelationshipStatus.SINGLE;
    transformed.relatedApplicantIndex = undefined;
    return transformed;
  });
}

/**
 * Reset relationship status to 'single' for any applicant
 * whose partner is no longer in the list (e.g. borrower was removed).
 */
export function cleanupStalePartnerReferences(applicants: ApplicantEntry[]): void {
  const validIds = new Set(applicants.map((a) => a.id));
  for (const applicant of applicants) {
    const rel = applicant.relationshipStatus as string | undefined;
    if (rel?.startsWith('married_to_')) {
      const partnerId = rel.replace('married_to_', '');
      if (partnerId !== 'non_applicant' && partnerId !== 'applicant' && !validIds.has(partnerId)) {
        applicant.relationshipStatus = 'single';
      }
    }
  }
}

/**
 * A composable to manage applicant marriage links.
 * Requirements:
 * 1. Show all other applicants in dropdown (no hiding)
 * 2. Sync both sides when a specific pairing is selected
 * 3. If unlinked, clear relationshipStatus
 */
export function useRelationshipManager<T extends RelationshipManagerApplicant>(applicants: Ref<T[]>) {
  const { getMaxSecondaryApplicants } = useFeatureFlags();

  /**
   * Get display name for an applicant (preferredName or firstName lastName)
   */
  function getApplicantDisplayName(applicantId: string, fallback = 'this applicant'): string {
    const applicant = applicants.value.find((a) => a.id === applicantId);
    if (!applicant) return fallback;
    return applicant.preferredName || [applicant.firstName, applicant.lastName].filter(Boolean).join(' ') || fallback;
  }

  /**
   * Build dropdown options for an applicant
   */
  function getRelationshipOptions(applicantId: string): Record<string, string> {
    const opts: Record<string, string> = {
      single: 'Single',
      married_to_non_applicant: 'Married/defacto to Non-Applicant',
      married_to_applicant: 'Married/defacto to Another Applicant',
    };

    // add all other applicants
    const secondaries = applicants.value.filter((a) => a.type !== 'primary');
    applicants.value.forEach((other) => {
      if (other.id === applicantId) return;
      const namedLabel = other.preferredName || [other.firstName, other.lastName].filter(Boolean).join(' ');
      let displayName: string;
      if (namedLabel) {
        displayName = namedLabel;
      } else if (other.type === 'primary') {
        displayName = 'Primary applicant';
      } else {
        const idx = secondaries.findIndex((a) => a.id === other.id);
        displayName = `Secondary applicant ${idx + 1}`;
      }
      opts[`married_to_${other.id}`] = `Married/defacto to ${displayName}`;
    });

    return opts;
  }

  /**
   * Get the required changes when an applicant's status changes
   * Returns an array of changes that need to be applied
   *
   * Scenarios:
   * 1. New pairing: applicant1 selects married_to_applicant2 → both get paired
   * 2. Change pairing: applicant1 changes from married_to_applicant2 to married_to_applicant3 → applicant2 becomes married_to_applicant, applicant3 gets paired
   * 3. Unpair: applicant1 changes from married_to_applicant2 to single/non-applicant → applicant2 becomes married_to_applicant
   */
  function getRequiredChanges(
    applicantId: string,
    newStatus: string,
    oldStatus?: string,
  ): Array<{ id: string; relationshipStatus: string }> {
    const changes: Array<{ id: string; relationshipStatus: string }> = [];

    // No-op: avoid emitting redundant changes
    if (newStatus === oldStatus) {
      return changes;
    }

    // SCENARIO 2 & 3: Handle breaking existing pairing
    if (oldStatus?.startsWith('married_to_')) {
      const oldPartnerId = oldStatus.replace('married_to_', '');

      // SCENARIO 2: If changing to a new specific pairing, set old partner to "married_to_applicant"
      const oldPartner = applicants.value.find((a) => a.id === oldPartnerId);
      if (oldPartner) {
        changes.push({ id: oldPartnerId, relationshipStatus: 'married_to_applicant' });
      }
    }

    // SCENARIO 1 & 2: Handle creating new pairing
    if (newStatus.startsWith('married_to_')) {
      const newPartnerId = newStatus.replace('married_to_', '');

      // SCENARIO 2: If new partner was already paired with someone else, break that pairing
      // First, check if the new partner is already married to someone specific
      const newPartner = applicants.value.find((a) => a.id === newPartnerId);
      if (newPartner?.relationshipStatus?.startsWith('married_to_')) {
        const existingPartnerId = newPartner.relationshipStatus.replace('married_to_', '');
        if (existingPartnerId !== applicantId) {
          // Break the existing pairing
          const existingPartner = applicants.value.find((a) => a.id === existingPartnerId);
          if (existingPartner) {
            changes.push({ id: existingPartnerId, relationshipStatus: 'married_to_applicant' });
          }
        }
      }

      // Update new partner's status to link back
      if (newPartner) {
        changes.push({ id: newPartnerId, relationshipStatus: `married_to_${applicantId}` });
      }
    }

    // Always update the current applicant's status
    changes.push({ id: applicantId, relationshipStatus: newStatus });

    return changes;
  }

  /**
   * Handle relationship changes and apply them to the correct applicants
   * This function can be used with any structure that has primary and secondary applicants
   */
  function handleRelationshipChange(
    applicantId: string,
    newStatus: string,
    oldStatus: string | undefined,
    primary: Ref<ApplicantEntry>,
    secondaries: Ref<ApplicantEntry[]>,
  ): void {
    const changes = getRequiredChanges(applicantId, newStatus, oldStatus);

    for (const change of changes) {
      if (change.id === primary.value.id) {
        primary.value.relationshipStatus = change.relationshipStatus as UIRelationshipStatus;
        continue;
      }

      const s = secondaries.value.find((x) => x.id === change.id);
      if (s) s.relationshipStatus = change.relationshipStatus as UIRelationshipStatus;
    }
  }

  /**
   * Validate reciprocal pairings
   */
  const validate = computed(() => {
    const errs: string[] = [];
    const list = applicants.value;

    // quick lookup
    const existingIds = new Set(list.map((a) => a.id));

    // 1) check maximum applicants
    const maxApplicantsTotal = getMaxSecondaryApplicants() + 1;
    if (list.length > maxApplicantsTotal) errs.push(`Maximum of ${maxApplicantsTotal} applicants allowed.`);

    // 2) disallow self-linking
    list.forEach((a) => {
      if (a.relationshipStatus === `married_to_${a.id}`) {
        errs.push(`${getApplicantDisplayName(a.id)} cannot be married to themselves.`);
      }
    });

    // 3) check for "married_to_applicant" status that needs specific pairing
    list.forEach((a) => {
      if (a.relationshipStatus === 'married_to_applicant') {
        errs.push(`[${a.id}] Add additional applicant or select existing`);
      }
    });

    // 4) check for invalid partner references (married to non-existent applicant)
    list.forEach((a) => {
      if (a.relationshipStatus?.startsWith('married_to_')) {
        const partnerId = a.relationshipStatus.replace('married_to_', '');
        if (partnerId !== 'non_applicant' && partnerId !== 'applicant') {
          const partnerExists = list.some((other) => other.id === partnerId);
          if (!partnerExists) {
            const name = a.preferredName || [a.firstName, a.lastName].filter(Boolean).join(' ') || 'this applicant';
            errs.push(
              `[${a.id}] ${name} is married to a partner who is no longer in the application. Please update the relationship status.`,
            );
          }
        }
      }
    });

    // 5) check for triangle relationships (A married to B, B married to C, C married to A)
    const marriageMap = new Map<string, string>();
    list.forEach((a) => {
      if (a.relationshipStatus?.startsWith('married_to_')) {
        const partnerId = a.relationshipStatus.replace('married_to_', '');
        if (partnerId !== 'non_applicant' && partnerId !== 'applicant') {
          marriageMap.set(a.id, partnerId);
        }
      }
    });

    // 6) enforce reciprocity and one-to-one constraint
    const inboundCount = new Map<string, number>();
    // count only valid, non-self links to existing partners
    for (const [aId, partnerId] of marriageMap) {
      if (partnerId === aId) continue; // already errored in step 2
      if (!existingIds.has(partnerId)) continue; // already errored in step 4
      inboundCount.set(partnerId, (inboundCount.get(partnerId) ?? 0) + 1);
    }

    inboundCount.forEach((count, partnerId) => {
      if (count > 1) {
        errs.push(
          `Multiple applicants are linked to ${getApplicantDisplayName(
            partnerId,
          )}. Each person can only be married to one other applicant.`,
        );
      }
    });

    // Reciprocity: if A -> B then B must -> A
    for (const [aId, partnerId] of marriageMap) {
      if (partnerId === aId) continue; // self-link already handled
      if (!existingIds.has(partnerId)) continue; // missing partner already handled
      if (marriageMap.get(partnerId) !== aId) {
        errs.push(
          `${getApplicantDisplayName(aId)}'s marriage must be reciprocal with ${getApplicantDisplayName(partnerId)}.`,
        );
      }
    }

    // Check for actual triangle relationships (not just reciprocal marriages)
    const visited = new Set<string>();
    for (const [applicantId] of marriageMap) {
      if (visited.has(applicantId)) continue;

      const chain = new Set<string>();
      let current: string | undefined = applicantId;
      let chainLength = 0;

      // use list.length + 1 as the bound (nice touch)
      while (current && !chain.has(current) && chainLength < list.length + 1) {
        chain.add(current);
        visited.add(current);
        current = marriageMap.get(current);
        chainLength++;
      }

      // Only flag as error if we have a cycle with more than 2 people (triangle or larger)
      if (current && chain.has(current) && chain.size > 2) {
        errs.push(
          `${getApplicantDisplayName(
            applicantId,
          )} is part of an invalid marriage chain. Each person can only be married to one other applicant.`,
        );
        break;
      }
    }

    return errs;
  });

  return {
    getRelationshipOptions,
    validate,
    getRequiredChanges,
    handleRelationshipChange,
  };
}
